import { usegetConditionHistory } from "@features/ax/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { History } from "lucide-react";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";

interface AssessmentRecord {
  id: string;
  date: string;
  scoreType: string;
  score: number;
  status: string;
  assessedBy: string;
  notes?: string;
}

const getStatusBadgeClass = (status: string): string => {
  switch (status.toLowerCase()) {
    case "excellent":
      return "bg-green-100 text-green-800";
    case "good":
      return "bg-blue-100 text-blue-800";
    case "fair":
      return "bg-yellow-100 text-yellow-800";
    case "poor":
      return "bg-red-100 text-red-800";
    case "critical":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export const AssessmentHistory = () => {
  const { systemId } = useParams();
  const facilityEntityType = 2;

  const [filters, setFilters] = useState({
    search: "",
    scoreType: "all",
    assessedBy: "all",
    status: "all",
    startDate: "",
    endDate: "",
    page: 1,
  });
  const PAGE_SIZE = 10;

  const { data: conditionHistory } = usegetConditionHistory(
    Number(systemId),
    facilityEntityType,
    filters.page
  );

  const columns: TableColumn<any>[] = useMemo(
    () => [
      {
        key: "assessmentTypeDisplay",
        header: "Score Type",
        type: "text",
        render: (value: string) => (
          <span className="font-medium ml-4">{value}</span>
        ),
      },
      {
        key: "score",
        header: "Score",
        type: "custom",
        render: (value: number, row: any) => (
          <span className="font-medium ml-4">
            {row.assessmentTypeDisplay === "Risk"
              ? row.riskScore
              : row.conditionScore}
          </span>
        ),
      },
      {
        key: "statusDisplay",
        header: "Status",
        type: "badge",
        badgeConfig: {
          getVariant: () => "secondary",
          getClassName: (value: string) => getStatusBadgeClass(value),
        },
        render: (value: string) => (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(
              value
            )}`}
          >
            {value}
          </span>
        ),
      },
      {
        key: "createdDate",
        header: "Assessment Date",
        type: "text",
      },
      {
        key: "createdUserRemoteName",
        header: "Assessed By",
        type: "text",
        render: (value: string) => (
          <span className="font-medium ml-4">{value}</span>
        ),
      },
    ],
    []
  );

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  // Pagination logic
  const totalItems = conditionHistory?.count;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startIndex = (filters.page - 1) * PAGE_SIZE;

  const pagination = useMemo(
    () => ({
      currentPage: filters.page,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: startIndex + 1,
      endItem: Math.min(filters.page * PAGE_SIZE, totalItems),
    }),
    [filters.page, totalPages, totalItems, startIndex]
  );

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div>
          <p className="page-title flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center">
              <History className="h-4 w-4 text-blue-600" />
            </div>
            Assessment History
          </p>
          <p className="page-subtitle mt-1">
            Complete history of all condition and risk assessments
          </p>
        </div>
      </div>

      <GenericTable
        data={conditionHistory?.results ?? []}
        columns={columns}
        rowKey="id"
        pagination={pagination}
        onPageChange={handlePageChange}
        emptyMessage="No assessment history found"
      />
    </div>
  );
};
